import { useEffect, useMemo, useState, type Dispatch, type ReactElement, type SetStateAction } from 'react';
import {
  emptyStructuredAddress,
  formatStructuredAddressForStorage,
  isStructuredAddressComplete
} from '../../addressFields';
import { AddressFieldsGroup, AddressReadOnly } from '../../AddressFieldsGroup';
import { VENDORS_API_URL } from '../../constants';
import type {
  DeliveryAddressDirectoryEntry,
  PoFormState,
  ProcurementRequest,
  VendorDirectoryEntry
} from '../../domain/types';
import { deliveryAddressesForRegion, mapApiVendor } from '../../domain/types';
import { materialInventoryNeed } from '../../domain/procurementRequest';
import type { RfqPoQuotePrefill } from '../../domain/requestForQuote';

export interface RequestPoCreateSectionProps {
  selectedRequest: ProcurementRequest;
  poForm: PoFormState;
  setPoForm: Dispatch<SetStateAction<PoFormState>>;
  vendors: VendorDirectoryEntry[];
  deliveryAddresses: DeliveryAddressDirectoryEntry[];
  onCreatePurchaseOrder: () => void | Promise<void>;
  onReloadVendors: () => void | Promise<void>;
  authFetch: (url: string, init?: RequestInit) => Promise<Response>;
  /** From an approved RFQ — pre-fills vendor on the PO form. */
  rfqSelectedVendorId?: string | null;
  /** From an approved RFQ — pre-fills incoterms when present. */
  rfqDefaultIncoterms?: string | null;
  /** From the awarded vendor’s submitted quote (when RFQ is approved). */
  rfqAwardedQuote?: RfqPoQuotePrefill | null;
}

export function RequestPoCreateSection({
  selectedRequest,
  poForm,
  setPoForm,
  vendors,
  deliveryAddresses,
  onCreatePurchaseOrder,
  onReloadVendors,
  authFetch,
  rfqSelectedVendorId,
  rfqDefaultIncoterms,
  rfqAwardedQuote
}: RequestPoCreateSectionProps): ReactElement {
  const [vendorPanelOpen, setVendorPanelOpen] = useState(false);
  const [nvName, setNvName] = useState('');
  const [nvAddr, setNvAddr] = useState(() => emptyStructuredAddress());
  const [nvError, setNvError] = useState('');
  const [nvSaving, setNvSaving] = useState(false);

  const requestWellRegion = (selectedRequest.wellInfo?.region ?? '').trim();
  const poDeliveryOptions = useMemo(
    () => deliveryAddressesForRegion(deliveryAddresses, requestWellRegion),
    [deliveryAddresses, requestWellRegion]
  );

  useEffect(() => {
    setPoForm((prev) => {
      if (!prev.deliveryAddressId) {
        return prev;
      }
      if (poDeliveryOptions.length === 0) {
        return prev;
      }
      if (poDeliveryOptions.some((d) => d.id === prev.deliveryAddressId)) {
        return prev;
      }
      return { ...prev, deliveryAddressId: '' };
    });
  }, [poDeliveryOptions, setPoForm]);

  useEffect(() => {
    setVendorPanelOpen(false);
    setNvName('');
    setNvAddr(emptyStructuredAddress());
    setNvError('');
  }, [selectedRequest.id, selectedRequest.wellInfo?.region]);

  useEffect(() => {
    const fromRequest = selectedRequest.deliveryAddressId?.trim() ?? '';
    const fromRfqVendor = rfqSelectedVendorId?.trim() ?? '';
    const dirVendor = fromRfqVendor ? vendors.find((v) => v.id === fromRfqVendor) : undefined;
    const q = rfqAwardedQuote;
    const inc = (q?.incoterms?.trim() || rfqDefaultIncoterms?.trim() || '').trim();
    setPoForm({
      vendorId: fromRfqVendor,
      vendorName: dirVendor?.name ?? '',
      deliveryAddressId: fromRequest,
      incoterms: inc,
      unitCost: q?.unitCost ?? '',
      lineDescription: q?.lineDescription ?? '',
      freightAmount: q?.freightAmount ?? '',
      miscellaneousFeeAmount: q?.miscellaneousFeeAmount ?? ''
    });
  }, [
    selectedRequest.id,
    selectedRequest.deliveryAddressId,
    rfqSelectedVendorId,
    rfqDefaultIncoterms,
    rfqAwardedQuote,
    vendors,
    setPoForm
  ]);

  function toggleVendorPanel(): void {
    setVendorPanelOpen((open) => {
      if (open) {
        return false;
      }
      setNvError('');
      setNvName('');
      setNvAddr(emptyStructuredAddress());
      return true;
    });
  }

  function closeVendorPanel(): void {
    setVendorPanelOpen(false);
  }

  async function saveNewVendor(): Promise<void> {
    setNvError('');
    if (!nvName.trim()) {
      setNvError('Vendor name is required.');
      return;
    }
    if (!isStructuredAddressComplete(nvAddr)) {
      setNvError('Enter a complete address (line 1, city, state/region, postal code, country).');
      return;
    }
    setNvSaving(true);
    try {
      const response = await authFetch(VENDORS_API_URL, {
        method: 'POST',
        body: JSON.stringify({
          name: nvName.trim(),
          address: formatStructuredAddressForStorage(nvAddr)
        })
      });
      if (!response.ok) {
        const text = await response.text();
        setNvError(text || 'Could not save vendor.');
        return;
      }
      const raw = (await response.json()) as Record<string, unknown>;
      const created = mapApiVendor(raw);
      await onReloadVendors();
      setPoForm((prev) => ({
        ...prev,
        vendorId: created.id,
        vendorName: created.name
      }));
      setVendorPanelOpen(false);
    } finally {
      setNvSaving(false);
    }
  }

  const selectedVendor = vendors.find((v) => v.id === poForm.vendorId);
  const selectedDeliveryAddress = deliveryAddresses.find((d) => d.id === poForm.deliveryAddressId);

  const primaryNeed = selectedRequest.materials[0]
    ? materialInventoryNeed(selectedRequest.materials[0])
    : null;

  return (
    <div className="panel-actions">
      <h4>Create purchase order</h4>
      <p className="hint">Issue a PO to place the order. Line items use remaining quantity after yard transfers.</p>
      {rfqSelectedVendorId ? (
        <p className="hint">
          Vendor, incoterms, freight, fees, and unit cost for the primary line are pre-filled from the{' '}
          <strong>awarded vendor's quote</strong> when the quote request is approved. The purchase order must use
          that vendor; creating the PO moves this request to <strong>Order placed</strong>.
        </p>
      ) : null}

      <h5 className="po-subheading full-width">Vendor</h5>
      <div className="full-width well-picker-row">
        <label className="well-picker-select">
          <span className="sr-only">Vendor</span>
          <select
            value={poForm.vendorId}
            onChange={(e) => {
              const id = e.target.value;
              if (!id) {
                setPoForm({ ...poForm, vendorId: '', vendorName: '' });
                return;
              }
              const v = vendors.find((x) => x.id === id);
              if (v) {
                setPoForm({ ...poForm, vendorId: v.id, vendorName: v.name });
              }
            }}
          >
            <option value="">Select a vendor…</option>
            {vendors.map((v) => (
              <option key={v.id} value={v.id}>
                {v.name}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          className="secondary well-add-btn"
          onClick={toggleVendorPanel}
          title={vendorPanelOpen ? 'Hide new vendor form' : 'Add a new vendor'}
          aria-expanded={vendorPanelOpen}
        >
          +
        </button>
      </div>
      {selectedVendor && (
        <div className="full-width">
          <p className="hint">Vendor address on file</p>
          <AddressReadOnly value={selectedVendor.address} />
        </div>
      )}

      <div className={`well-add-slide-panel full-width ${vendorPanelOpen ? 'is-open' : ''}`}>
        <div className="well-add-slide-inner">
          <div className="card nested-card well-add-panel-body">
            <div className="row spread">
              <h4 className="form-section-title well-add-panel-title">New vendor</h4>
              <button type="button" className="secondary icon-close" onClick={closeVendorPanel} aria-label="Close">
                ×
              </button>
            </div>
            <p className="hint">Saved vendors appear in the dropdown for all procurement users.</p>
            <div className="grid">
              <input
                placeholder="Vendor name"
                value={nvName}
                onChange={(e) => setNvName(e.target.value)}
                autoComplete="organization"
              />
              <AddressFieldsGroup
                idPrefix="po-new-vendor"
                legend="Vendor address"
                value={nvAddr}
                onChange={setNvAddr}
              />
              {nvError && <p className="error full-width">{nvError}</p>}
              <div className="row full-width">
                <button type="button" disabled={nvSaving} onClick={() => void saveNewVendor()}>
                  {nvSaving ? 'Saving…' : 'Save vendor'}
                </button>
                <button type="button" className="secondary" disabled={nvSaving} onClick={closeVendorPanel}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <h5 className="po-subheading full-width">Delivery address</h5>
      <div className="full-width well-picker-row">
        <label className="well-picker-select">
          <span className="sr-only">Delivery address</span>
          <select
            value={poForm.deliveryAddressId}
            disabled={!requestWellRegion}
            title={
              requestWellRegion
                ? 'Delivery locations for this request’s well region'
                : 'Request must have a well with a region to choose a delivery address'
            }
            onChange={(e) => {
              const id = e.target.value;
              setPoForm({ ...poForm, deliveryAddressId: id });
            }}
          >
            <option value="">
              {!requestWellRegion
                ? 'Request well must have a region…'
                : poDeliveryOptions.length === 0
                  ? 'No delivery addresses for this region…'
                  : 'Select a delivery address…'}
            </option>
            {poDeliveryOptions.map((d) => (
              <option key={d.id} value={d.id}>
                {d.label}
              </option>
            ))}
          </select>
        </label>
      </div>
      {selectedDeliveryAddress && (
        <div className="full-width">
          <p className="hint">Address on file</p>
          <AddressReadOnly value={selectedDeliveryAddress.address} />
        </div>
      )}
      <p className="hint full-width">
        New delivery locations are maintained under <strong>Admin → Regions</strong>.
      </p>
      <label className="full-width">
        Freight (USD, optional)
        <input
          type="number"
          min={0}
          step="0.01"
          value={poForm.freightAmount}
          onChange={(e) => setPoForm({ ...poForm, freightAmount: e.target.value })}
          placeholder="0"
        />
      </label>
      <label className="full-width">
        Miscellaneous fee (USD, optional)
        <input
          type="number"
          min={0}
          step="0.01"
          value={poForm.miscellaneousFeeAmount}
          onChange={(e) => setPoForm({ ...poForm, miscellaneousFeeAmount: e.target.value })}
          placeholder="0"
        />
      </label>
      <input
        placeholder="Incoterms (e.g. FOB, CIF, DDP)"
        value={poForm.incoterms}
        onChange={(e) => setPoForm({ ...poForm, incoterms: e.target.value })}
      />
      <input
        type="number"
        min={0}
        step="0.01"
        placeholder="Unit cost (USD)"
        value={poForm.unitCost}
        onChange={(e) => setPoForm({ ...poForm, unitCost: e.target.value })}
      />
      <input
        placeholder="Line description override (optional; default is size · length · grade from casing)"
        value={poForm.lineDescription}
        onChange={(e) => setPoForm({ ...poForm, lineDescription: e.target.value })}
      />
      <p className="hint">
        Primary line: {selectedRequest.materials[0]?.name ?? '—'} · Qty{' '}
        {primaryNeed
          ? `${primaryNeed.remaining.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${primaryNeed.unit}${
              primaryNeed.fulfilled > 0 ? ' remaining' : ''
            }`
          : '—'}
      </p>
      <button type="button" onClick={() => void onCreatePurchaseOrder()}>
        Create purchase order
      </button>
    </div>
  );
}
